import { cn } from "../../lib/utils";
import type { FilterState, Owner, CardStatus } from "../../data/types";

interface FilterBarProps {
  filters: FilterState;
  onChange: (updates: Partial<FilterState>) => void;
  onReset: () => void;
  showLaneFilter?: boolean;
  showWeekFilter?: boolean;
  className?: string;
}

const selectCls = cn(
  "h-[32px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
  "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)] pr-[var(--spacing-6)]",
  "text-[14px] text-[var(--color-text-primary)] appearance-none cursor-pointer",
  "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
  "hover:border-[var(--color-bg-hover)] transition-colors duration-[var(--duration-fast)]",
);

const inputCls = cn(
  "h-[32px] w-[200px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
  "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)]",
  "text-[14px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]",
  "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
  "hover:border-[var(--color-bg-hover)] transition-colors duration-[var(--duration-fast)]",
);

export function FilterBar({
  filters,
  onChange,
  onReset,
  showWeekFilter = true,
  className,
}: FilterBarProps) {
  const hasFilters =
    filters.owner !== "all" ||
    filters.status !== "all" ||
    filters.week !== "all" ||
    filters.search !== "";

  return (
    <div className={cn("flex items-center gap-[var(--spacing-2)] flex-wrap", className)}>
      {/* Search */}
      <input
        type="text"
        placeholder="Search…"
        value={filters.search}
        onChange={(e) => onChange({ search: e.target.value })}
        className={inputCls}
      />

      {/* Owner */}
      <select
        value={filters.owner}
        onChange={(e) => onChange({ owner: e.target.value as Owner | "all" })}
        className={selectCls}
      >
        <option value="all">All Owners</option>
        <option value="Joel">Joel</option>
        <option value="Albert">Albert</option>
        <option value="AI Wave">AI Wave</option>
        <option value="Joint">Joint</option>
      </select>

      {/* Status */}
      <select
        value={filters.status}
        onChange={(e) => onChange({ status: e.target.value as CardStatus | "all" })}
        className={selectCls}
      >
        <option value="all">All Statuses</option>
        <option value="planned">Planned</option>
        <option value="in_progress">In Progress</option>
        <option value="blocked">Blocked</option>
        <option value="done">Done</option>
      </select>

      {/* Week */}
      {showWeekFilter && (
        <select
          value={filters.week === "all" ? "all" : String(filters.week)}
          onChange={(e) => onChange({ week: e.target.value === "all" ? "all" : Number(e.target.value) })}
          className={selectCls}
        >
          <option value="all">All Weeks</option>
          <option value="0">W0 — Pre-launch</option>
          <option value="1">W1 — Launch</option>
          <option value="2">W2 — Optimize</option>
          <option value="3">W3 — Scale</option>
          <option value="4">W4 — Convert</option>
        </select>
      )}

      {/* Reset */}
      {hasFilters && (
        <button
          onClick={onReset}
          className={cn(
            "h-[32px] px-[var(--spacing-3)] rounded-[var(--radius-md)]",
            "text-[14px] text-[var(--color-accent)] hover:bg-[var(--color-accent-subtle-bg)]",
            "transition-colors duration-[var(--duration-fast)] cursor-pointer",
            "animate-chip-in",
          )}
        >
          Clear
        </button>
      )}
    </div>
  );
}
